import { useEffect, useState } from "react";
import { api } from "../../lib/api";

type Connection = {
  id: string;
  platform: string;
  is_active: boolean;
  connected_at: string;
  last_sync_at: string | null;
};

type Provider = {
  id: string;
  label: string;
  icon: string;
  blurb: string;
  status: "available" | "coming_soon";
};

// Phase 2C-1: all marked available since they all go to the stub.
// Phase 2C-2: status reflects whether the OAuth app has been approved per provider.
const PROVIDERS: Provider[] = [
  { id: "apple_health",        label: "Apple Health",        icon: "🍎", blurb: "iPhone + Apple Watch — HRV, sleep, activity, heart rate",          status: "available" },
  { id: "oura",                label: "Oura Ring",           icon: "💍", blurb: "Sleep stages, HRV, readiness, body temp",                          status: "available" },
  { id: "whoop",               label: "WHOOP",               icon: "⌚", blurb: "Strain, recovery, sleep performance, HRV",                         status: "available" },
  { id: "garmin",              label: "Garmin",              icon: "⌚", blurb: "Heart rate, Body Battery, sleep, stress, steps",                  status: "available" },
  { id: "google_health",       label: "Fitbit · Pixel Watch", icon: "⌚", blurb: "Fitbit and Google Pixel Watch via Google Health API",         status: "available" },
  { id: "strava",              label: "Strava",              icon: "🏃", blurb: "Workouts, training load, performance trends",                     status: "coming_soon" },
];

type Props = {
  open: boolean;
  onClose: () => void;
  onChange?: () => void;
};

export function WearableConnectionsPanel({ open, onClose, onChange }: Props) {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { connections } = await api.wearables.list();
      setConnections(connections);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const connStatus = (id: string) => connections.find((c) => c.platform === id && c.is_active);

  const connect = async (platform: string) => {
    setPending(platform);
    try {
      const result: any = await api.wearables.connect(platform);
      if (result.mode === "oauth_redirect" && result.oauth_url) {
        // Real OAuth: hand off to the provider — user returns to dashboard after authorizing
        window.location.href = result.oauth_url;
        return;  // Don't reset pending — we're leaving the page
      }
      await load();
      onChange?.();
    } catch (e: any) {
      console.error("[connect] failed:", e);
      alert(`Couldn't connect ${platform}: ${e?.message ?? "unknown error"}`);
    } finally { setPending(null); }
  };

  const disconnect = async (platform: string) => {
    setPending(platform);
    try {
      await api.wearables.disconnect(platform);
      await load();
      onChange?.();
    } finally { setPending(null); }
  };

  const sync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await api.wearables.sync();
      // Chain YWI recompute — wearable signals feed Physical/Emotional/Mental dimensions
      try { await api.ywi.recompute(); } catch { /* non-fatal */ }
      setSyncResult(`Synced ${result.synced} day records — YWI refreshed`);
      onChange?.();
    } catch (e: any) {
      setSyncResult("Sync failed — try again.");
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 4000);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end animate-[fadeUp_0.2s_ease]" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative h-full w-full max-w-md bg-d_sf border-l border-d_border/20 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-d_sf border-b border-d_border/20 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[9px] tracking-[0.32em] text-d_accent font-semibold uppercase">Wearables</div>
            <h2 className="font-serif text-xl font-semibold mt-0.5">Connected Devices</h2>
          </div>
          <button onClick={onClose} className="btn-ghost text-base w-7 h-7">✕</button>
        </div>

        <div className="px-5 py-4">
          {connections.filter((c) => c.is_active).length > 0 && (
            <div className="mb-5 flex items-center gap-2">
              <button
                onClick={sync}
                disabled={syncing}
                className="btn-primary"
              >
                {syncing ? "Syncing…" : "Sync now"}
              </button>
              {syncResult && <span className="text-[10px] text-d_celeste">{syncResult}</span>}
            </div>
          )}

          {loading ? (
            <div className="text-xs text-d_text3 italic py-6 text-center">Loading…</div>
          ) : (
            <div className="space-y-1.5">
              {PROVIDERS.map((p) => {
                const conn = connStatus(p.id);
                const isPending = pending === p.id;
                const comingSoon = p.status === "coming_soon";
                return (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      conn ? "border-d_accent/30 bg-d_accent/[.04]" : "border-d_border/20 bg-d_card/50"
                    } ${comingSoon ? "opacity-50" : ""}`}
                  >
                    <div className="text-2xl flex-shrink-0">{p.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{p.label}</span>
                        {conn && <span className="text-[8px] tracking-wider uppercase text-d_accent font-semibold">Connected</span>}
                        {comingSoon && <span className="text-[8px] tracking-wider uppercase text-d_text3">Coming soon</span>}
                      </div>
                      <div className="text-[10px] text-d_text3 mt-0.5 truncate">{p.blurb}</div>
                      {conn?.last_sync_at && (
                        <div className="text-[9px] text-d_celeste mt-1">
                          Last sync: {new Date(conn.last_sync_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => conn ? disconnect(p.id) : connect(p.id)}
                      disabled={comingSoon || isPending}
                      className={
                        conn
                          ? "btn-danger flex-shrink-0"
                          : comingSoon
                            ? "inline-flex items-center justify-center text-[10px] font-medium uppercase tracking-[0.10em] px-3 py-2 rounded-md border border-d_border/40 text-d_text3 cursor-not-allowed flex-shrink-0"
                            : "btn-primary flex-shrink-0"
                      }
                    >
                      {isPending ? "…" : conn ? "Disconnect" : "Connect"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 px-3.5 py-3 bg-d_card2 border border-d_border/40 rounded-md">
            <div className="text-[10px] tracking-[0.24em] uppercase text-d_accent font-semibold mb-1.5">Privacy</div>
            <p className="text-[11px] text-d_text2 leading-relaxed">
              Provider OAuth tokens are encrypted at rest. We pull only the metrics LifeScribe™ uses to build your scores. Disconnect anytime — we'll stop receiving new data immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
